import { Step } from "../return-flow";
import { FlowElementTranslatableField } from "./flow-element-translation";

type StepTypeSymbol = Extract<(typeof Step)[keyof typeof Step], symbol>;

/** Field names double as model property names; add a mapping if that breaks. */
export interface FlowElementLocus {
  idProperty: string;
  fields: readonly FlowElementTranslatableField[];
  meaningfulFields?: readonly FlowElementTranslatableField[];
  sourcePaths?: Partial<
    Record<FlowElementTranslatableField, readonly string[]>
  >;
}

export interface StepElementSpec {
  step?: FlowElementLocus;
  lists?: readonly (FlowElementLocus & { property: string })[];
}

/**
 * Which flow elements are translatable, keyed by Step symbol so a new step
 * cannot compile until someone decides; `null` is that decision, not a gap.
 */
export const FLOW_ELEMENT_ID_SPECS: Record<
  StepTypeSymbol,
  StepElementSpec | null
> = {
  [Step.MULTIPLE_CHOICE]: {
    step: { idProperty: "stepUUID", fields: ["message", "description"] },
    lists: [
      { property: "options", idProperty: "optionUUID", fields: ["name"] },
    ],
  },
  [Step.ACTION]: null,
  [Step.ADD_CONVERSATION_TAG]: null,
  [Step.ADD_EXCHANGE_ORDER_TAGS]: null,
  [Step.ADD_RETURN_TAG]: null,
  [Step.ADD_UNDECLARED_ITEM]: null,
  [Step.AB_TEST]: null,
  [Step.AI_RETURN_REASONS]: {
    step: {
      idProperty: "stepUUID",
      fields: ["customQuestion", "customExplanationSubtext"],
      meaningfulFields: ["customQuestion", "customExplanationSubtext"],
    },
  },
  [Step.ASSIGN_TO_AGENT]: null,
  [Step.BLOCK]: { step: { idProperty: "stepUUID", fields: ["message"] } },
  [Step.CHAT_MULTIPLE_CHOICE]: null,
  [Step.CLOSE_CHAT]: null,
  [Step.COMBINE_LABEL]: null,
  [Step.CONDITION]: null,
  [Step.CUSTOMER_RESPONSE]: null,
  [Step.DISCOUNT_CODE_NAME]: null,
  [Step.DO_NOTHING]: null,
  [Step.DYNAMIC_BUNDLE_ADJUSTMENT]: null,
  [Step.EVIDENCE]: null,
  [Step.EW_UPSELL]: null,
  [Step.EXCHANGE_SHIPPING_NAME]: null,
  [Step.EXCHANGE_SHIPPING_RATES]: null,
  [Step.FINAL_SALE_RETURN]: null,
  [Step.FLAG]: null,
  [Step.INFORMATION]: {
    step: {
      idProperty: "stepUUID",
      fields: ["header", "message", "continueButtonText"],
    },
  },
  [Step.INPUT]: {
    lists: [
      {
        property: "parts",
        idProperty: "partUUID",
        fields: ["message", "description", "customRegexErrorMessage"],
        meaningfulFields: ["customRegexErrorMessage"],
        sourcePaths: {
          customRegexErrorMessage: [
            "textValidationSettings",
            "customRegexErrorMessage",
          ],
        },
      },
    ],
  },
  [Step.MANUAL_REVIEW]: null,
  [Step.MERCHANT_REVIEW_BEFORE_FILING]: null,
  [Step.MULTIPLE_ACTIONS]: null,
  [Step.MULTIPLE_CONDITIONS]: null,
  [Step.OVERRIDE_ADJUSTMENT]: null,
  [Step.OVERRIDE_GREEN_RETURN]: null,
  [Step.PROCESSING_ACTION]: null,
  [Step.REDO_MANAGED_LOCATION]: null,
  [Step.REENTRY]: null,
  [Step.REJECT]: { step: { idProperty: "stepUUID", fields: ["message"] } },
  [Step.RETURN]: null,
  [Step.RETURN_PROCESSING_DECISION]: null,
  [Step.RETURN_PROCESSING_INFORMATION]: null,
  [Step.SEE_ORDERS]: null,
  [Step.SEND_EMAIL]: null,
  [Step.SEND_MESSAGE]: null,
  [Step.SEND_SMS]: null,
  [Step.SEPARATE_LABELS]: null,
  [Step.SET_RETURN_LOCATIONS]: null,
  [Step.SORT]: null,
  [Step.SUBMIT_CLAIM]: null,
  [Step.SUBMIT_REPAIR]: null,
  [Step.SUBMIT_WARRANTY]: null,
  [Step.SUBMIT_WARRANTY_REGISTRATION]: null,
  [Step.TRIGGER]: null,
  [Step.VERIFICATION]: null,
  [Step.VERIFY_ITEM_VARIANT]: null,
  [Step.MAKE_SERVICE_REQUEST]: null,
  [Step.TRANSFORM_PRODUCT]: null,
  [Step.WAIT]: null,
};

/** Wire type is `symbol.description`, enforced by symbolUnionJsonFormat. */
const SPECS_BY_WIRE_TYPE = new Map<string, StepElementSpec>();
const SPECS_BY_SYMBOL = new Map<symbol, StepElementSpec>();
for (const value of Object.values(Step)) {
  if (typeof value !== "symbol" || !value.description) {
    continue;
  }
  const spec = FLOW_ELEMENT_ID_SPECS[value];
  if (spec) {
    SPECS_BY_WIRE_TYPE.set(value.description, spec);
    SPECS_BY_SYMBOL.set(value, spec);
  }
}

export const TRANSLATABLE_WIRE_TYPES: readonly string[] = [
  ...SPECS_BY_WIRE_TYPE.keys(),
];

export const TRANSLATABLE_WIRE_SPECS: readonly {
  wireType: string;
  spec: StepElementSpec;
}[] = [...SPECS_BY_WIRE_TYPE].map(([wireType, spec]) => ({ wireType, spec }));

export const TRANSLATABLE_STEP_SYMBOLS: readonly symbol[] = [
  ...SPECS_BY_SYMBOL.keys(),
];

/** The step or one list entry, whichever owns the id and the text. */
export interface FlowElement {
  target: object;
  idProperty: string;
  id: string | undefined;
  fields: readonly FlowElementTranslatableField[];
  sourceValue(field: FlowElementTranslatableField): unknown;
}

function readProperty(target: object, property: string): unknown {
  return Reflect.get(target, property);
}

export function flowElementSourcePath(
  locus: FlowElementLocus,
  field: FlowElementTranslatableField,
): readonly string[] {
  return locus.sourcePaths?.[field] ?? [field];
}

export function flowElementSourceValue(
  target: object,
  locus: FlowElementLocus,
  field: FlowElementTranslatableField,
): unknown {
  let value: unknown = target;
  for (const property of flowElementSourcePath(locus, field)) {
    if (typeof value !== "object" || value === null) {
      return undefined;
    }
    value = readProperty(value, property);
  }
  return value;
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

export function flowElementSpecOfStep(step: object): StepElementSpec | null {
  const type = readProperty(step, "type");
  if (typeof type === "symbol") {
    return SPECS_BY_SYMBOL.get(type) ?? null;
  }
  if (typeof type === "string") {
    return SPECS_BY_WIRE_TYPE.get(type) ?? null;
  }
  return null;
}

function elementAt(target: object, locus: FlowElementLocus): FlowElement {
  return {
    target,
    idProperty: locus.idProperty,
    id: stringOrUndefined(readProperty(target, locus.idProperty)),
    fields: locus.fields,
    sourceValue: (field) => flowElementSourceValue(target, locus, field),
  };
}

/** Accepts a model step (symbol `type`) or a serialized one (string `type`). */
export function flowElementsOfStep(step: unknown): FlowElement[] {
  if (typeof step !== "object" || step === null) {
    return [];
  }
  const spec = flowElementSpecOfStep(step);
  if (!spec) {
    return [];
  }
  const elements: FlowElement[] = [];
  if (spec.step) {
    elements.push(elementAt(step, spec.step));
  }
  for (const list of spec.lists ?? []) {
    const entries = readProperty(step, list.property);
    if (!Array.isArray(entries)) {
      continue;
    }
    for (const entry of entries) {
      if (typeof entry === "object" && entry !== null) {
        elements.push(elementAt(entry, list));
      }
    }
  }
  return elements;
}

export function flowElementsOfSteps(steps: readonly unknown[]): FlowElement[] {
  return steps.flatMap(flowElementsOfStep);
}

export function flowElementIdsOfSteps(steps: readonly unknown[]): string[] {
  const ids = new Set<string>();
  for (const element of flowElementsOfSteps(steps)) {
    if (element.id) {
      ids.add(element.id);
    }
  }
  return [...ids];
}

/**
 * Mutates steps in place: the first element keeps a repeated id, each later
 * one gets a fresh uuid. Returns the count reminted.
 */
export function remintDuplicateElementIds(steps: readonly unknown[]): number {
  const seen = new Set<string>();
  let reminted = 0;
  for (const element of flowElementsOfSteps(steps)) {
    if (!element.id) {
      continue;
    }
    if (seen.has(element.id)) {
      Reflect.set(element.target, element.idProperty, crypto.randomUUID());
      reminted++;
    } else {
      seen.add(element.id);
    }
  }
  return reminted;
}
